import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import Select from "react-select";
import Navbar from "../../components/Navbar";
import Sidebar from "../../components/Sidebar";
import Footer from "../../components/Footer";
import DataTable from "../../components/DataTable";
import { api } from "../../axios/api";
import { Calendar, User, ArrowUpRight, ArrowDownLeft, RefreshCw, DollarSign, Scale, FileText, Printer, X } from "lucide-react";

const ContaCorrente = () => {
    const user = useSelector((state) => state.user.user);
    const language = useSelector((state) => state.language?.currentLanguage || "pt-AO");
    const empresaId = user?.empresa_id;

    const baseURL = api.defaults.baseURL; 

    const hojeStr = new Date().toISOString().split('T')[0];
    const trintaDiasAtrasStr = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];

    const [dataInicio, setDataInicio] = useState(trintaDiasAtrasStr);
    const [dataFim, setDataFim] = useState(hojeStr);
    const [clientes, setClientes] = useState([]);
    const [clienteSelecionado, setClienteSelecionado] = useState(null);
    
    const [movimentos, setMovimentos] = useState([]);
    const [resumo, setResumo] = useState({
        totalDebito: 0,
        totalRetencao: 0, 
        totalCredito: 0,
        saldoDebitoPendente: 0
    });
    const [loading, setLoading] = useState(false);

    // ESTADOS PARA O MODAL DE SELEÇÃO DE IMPRESSORA TÉRMICA
    const [modalAberto, setModalAberto] = useState(false);
    const [impressoras, setImpressoras] = useState([]);
    const [loadingImpressoras, setLoadingImpressoras] = useState(false);
    const [loadingImpressao, setLoadingImpressao] = useState(false);

    const texts = {
        "pt-AO": {
            title: "Conta Corrente de Clientes",
            subtitle: "Consulte o extrato detalhado, histórico de débitos, créditos e saldo acumulado por cliente",
            lblStart: "Data Inicial",
            lblEnd: "Data Final",
            lblCliente: "Selecionar Cliente",
            lblTodos: "Pesquise ou escolha um Cliente...",
            btnFilter: "Filtrar",
            btnPdf: "Exportar PDF",
            btnTicket: "Imprimir Ticket",
            cardDebito: "Total Débitos (Faturado)",
            cardRetencao: "Retenções na Fonte", 
            cardCredito: "Total Créditos (Amortizado)",
            cardSaldo: "Saldo em Aberto",
            colData: "DATA",
            colDoc: "DOCUMENTO / REF",
            colTipo: "TIPO",
            colDebito: "DÉBITO (+)",
            colCredito: "CRÉDITO (-)",
            colSaldoProg: "SALDO ACUMULADO",
            modalTitle: "Selecionar Impressora",
            modalSubtitle: "Escolha uma impressora térmica activa para imprimir o ticket do extrato",
            btnEnviarImpressao: "Imprimir Agora",
            lblNenhumaImpressora: "Nenhuma impressora encontrada para esta empresa."
        }
    };

    const t = texts[language] || texts["pt-AO"];

    // Carrega a listagem de clientes vinculados à empresa
    useEffect(() => {
        if (empresaId) {
            api.get(`/app/clientes/empresa/${empresaId}`)
                .then(res => {
                    const dadosClientes = res.data || [];
                    const options = dadosClientes.map(c => ({
                        value: c.id,
                        label: `${c.nome.toUpperCase()} (${c.nif || 'S/NIF'})`
                    }));
                    setClientes(options);
                })
                .catch(err => console.error("Erro ao buscar clientes:", err));
        }
    }, [empresaId]);

    // Busca o extrato de conta corrente do cliente selecionado
    const fetchContaCorrente = async () => {
        if (!clienteSelecionado) return;
        setLoading(true);
        try {
            const response = await api.get(`/app/relatorios/conta-corrente/${empresaId}`, {
                params: {
                    data_inicio: dataInicio,
                    data_fim: dataFim,
                    cliente_id: clienteSelecionado.value
                }
            });
            
            if (response.data) {
                const listaMovimentos = response.data.movimentos || [];
                
                let saldoAcumulado = 0;
                const movimentosComSaldo = listaMovimentos.map(mov => {
                    const debito = parseFloat(mov.total_geral || 0);
                    const creditoReal = parseFloat(mov.credito_documento || 0);
                    
                    saldoAcumulado += (debito - creditoReal);

                    return {
                        ...mov,
                        saldo_acumulado: Math.max(0, saldoAcumulado)
                    };
                });

                setMovimentos(movimentosComSaldo);
                setResumo(response.data.totais || { totalDebito: 0, totalRetencao: 0, totalCredito: 0, saldoDebitoPendente: 0 });
            }
        } catch (error) {
            console.error("Erro ao buscar conta corrente:", error);
        } finally {
            setLoading(false);
        }
    };

    // BUSCA PARQUE DE IMPRESSORAS DA EMPRESA E ABRE O MODAL
    const abrirModalImpressora = async () => {
        if (!clienteSelecionado || movimentos.length === 0) return;
        setModalAberto(true);
        setLoadingImpressoras(true);
        try {
            const res = await api.get(`/app/impressoras/empresa/${empresaId}`);
            setImpressoras(res.data || []);
        } catch (error) {
            console.error("Erro ao carregar impressoras:", error);
        } finally {
            setLoadingImpressoras(false);
        }
    };

    // ENVIA O COMANDO DE IMPRESSÃO DO TICKET COMPLETO DA CONTA CORRENTE PARA O BACKEND
    const dispararImpressaoTicket = async (impressoraId) => {
        if (!clienteSelecionado) return;
        setLoadingImpressao(true);
        try {
            await api.post(`/app/prints/ticket-conta-corrente`, {
                empresa_id: empresaId,
                cliente_id: clienteSelecionado.value,
                data_inicio: dataInicio,
                data_fim: dataFim,
                impressora_id: impressoraId
            });
            setModalAberto(false);
        } catch (error) {
            console.error("Erro ao emitir ticket na impressora térmica:", error);
            alert("Não foi possível estabelecer comunicação com a impressora térmica selecionada.");
        } finally {
            setLoadingImpressao(false);
        }
    };

    const formatCurrency = (val) => {
        return new Intl.NumberFormat("pt-AO", { style: "currency", currency: "AOA" }).format(val || 0);
    };

    // Link estático de abertura do PDF do Backend
    const pdfUrl = clienteSelecionado 
        ? `${baseURL}/prints/conta-corrente?empresa_id=${empresaId}&cliente_id=${clienteSelecionado.value}&data_inicio=${dataInicio}&data_fim=${dataFim}`
        : "#";

    const customSelectStyles = {
        control: (provided, state) => ({
            ...provided,
            borderRadius: "0px",
            border: state.isFocused ? "1px solid #0078d4" : "1px solid #8a8886",
            boxShadow: "none",
            minHeight: "34px",
            height: "34px",
            fontSize: "13px",
            background: "#fff",
            '&:hover': { borderColor: state.isFocused ? "#0078d4" : "#323130" }
        }),
        valueContainer: (provided) => ({ ...provided, padding: "0px 8px", height: "34px" }),
        indicatorsContainer: (provided) => ({ ...provided, height: "34px" }),
        menu: (provided) => ({ ...provided, borderRadius: "0px", marginTop: "1px", zIndex: 9999 }),
        option: (provided, state) => ({
            ...provided,
            fontSize: "13px",
            backgroundColor: state.isSelected ? "#0078d4" : state.isFocused ? "#f3f2f1" : "#fff",
            color: state.isSelected ? "#fff" : "#323130",
            cursor: "pointer",
            '&:active': { backgroundColor: "#0078d4", color: "#fff" }
        })
    };

    return (
        <div id="layout-wrapper">
            <Navbar />
            <Sidebar />

            <style>{`
                .win10-layout { min-height: 90vh; padding: 0px 0px; }
                .win10-panel-title { font-size: 18px; color: #323130; font-weight: 600; text-transform: uppercase; letter-spacing: 0.5px; }
                .win10-card-main { background: #fff; border: 1px solid #e1e1e1; border-radius: 0px; padding: 20px; margin-bottom: 25px; }
                .kpi-container { display: flex; gap: 12px; margin-bottom: 20px; flex-wrap: wrap; }
                .kpi-box { background: #faf9f8; border: 1px solid #d2d0ce; padding: 15px 18px; flex: 1; min-width: 200px; border-left: 4px solid #a80000; }
                .kpi-box.kpi-retencao { border-left-color: #c4641c; }
                .kpi-box.kpi-credito { border-left-color: #107c41; }
                .kpi-box.kpi-saldo { border-left-color: #0078d4; }
                .kpi-box.kpi-saldo-devedor { border-left-color: #e81123; }
                .kpi-title { font-size: 11px; color: #605e5c; text-transform: uppercase; font-weight: bold; }
                .kpi-value { font-size: 20px; color: #323130; font-weight: 700; font-family: monospace; margin-top: 4px; }
                .win10-input { border-radius: 0px; border: 1px solid #8a8886; font-size: 13px; padding: 5px 10px; height: 34px; background: #fff; }
                
                .win10-btn-filter { background: #323130; color: #fff; border-radius: 0px; font-size: 12px; font-weight: 600; height: 34px; padding: 0 20px; border: none; text-transform: uppercase; transition: 0.1s; display: inline-flex; align-items: center; gap: 6px; }
                .win10-btn-filter:hover { background: #201f1e; }
                .win10-btn-filter:disabled { background: #f3f2f1; color: #a19f9d; cursor: not-allowed; }
                
                .win10-link-pdf { background: #a80000; color: #fff !important; border-radius: 0px; font-size: 12px; font-weight: 600; height: 34px; padding: 0 20px; display: inline-flex; align-items: center; gap: 8px; text-transform: uppercase; text-decoration: none; transition: 0.1s; border: none; }
                .win10-link-pdf:hover { background: #800000; text-decoration: none; }
                .win10-link-pdf.disabled { background: #f3f2f1; color: #a19f9d !important; cursor: not-allowed; pointer-events: none; }
                
                .win10-btn-ticket { background: #107c41; color: #fff; border-radius: 0px; font-size: 12px; font-weight: 600; height: 34px; padding: 0 20px; display: inline-flex; align-items: center; gap: 8px; text-transform: uppercase; transition: 0.1s; border: none; cursor: pointer; }
                .win10-btn-ticket:hover { background: #0b592e; }
                .win10-btn-ticket:disabled { background: #f3f2f1; color: #a19f9d; cursor: not-allowed; }

                .win10-modal-overlay { position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0,0,0,0.4); display: flex; align-items: center; justify-content: center; z-index: 10050; }
                .win10-modal-box { background: #fff; border: 1px solid #0078d4; min-width: 450px; box-shadow: 0 4px 15px rgba(0,0,0,0.2); border-radius: 0px; padding: 0; }
                .win10-modal-header { background: #f3f2f1; padding: 10px 15px; border-bottom: 1px solid #e1e1e1; display: flex; justify-content: space-between; align-items: center; }
                .win10-modal-body { padding: 20px; }
                .printer-row { padding: 10px; border: 1px solid #d2d0ce; margin-bottom: 8px; display: flex; justify-content: space-between; align-items: center; transition: 0.1s; }
                .printer-row:hover { border-color: #0078d4; background: #f3f2f1; }
                
                .badge-cc { font-size: 10px; padding: 3px 8px; font-weight: bold; text-transform: uppercase; color: #fff; display: inline-block; }
                .badge-cc.cc-debito { background: #e81123; }
            `}</style>

            <main className="app-wrapper animate__animated animate__fadeIn">
                <div className="win10-layout">
                    
                    <div className="win10-card-main shadow-sm">
                        <div className="d-flex justify-content-between align-items-center flex-wrap gap-3 mb-4 border-bottom pb-3">
                            <div>
                                <h2 className="win10-panel-title m-0 d-flex align-items-center gap-2">
                                    <Scale size={20} className="text-dark"/> {t.title}
                                </h2>
                                <p className="text-muted small m-0 mt-1">{t.subtitle}</p>
                            </div>
                            
                            <div className="d-flex align-items-end gap-2 flex-wrap">
                                <div className="d-flex flex-column" style={{ minWidth: "260px" }}>
                                    <label className="text-muted small fw-bold mb-1"><User size={12} className="inline mr-1"/> {t.lblCliente}</label>
                                    <Select 
                                        options={clientes}
                                        value={clienteSelecionado}
                                        onChange={(option) => setClienteSelecionado(option)}
                                        placeholder={t.lblTodos}
                                        isClearable={true}
                                        styles={customSelectStyles}
                                    />
                                </div>
                                <div className="d-flex flex-column">
                                    <label className="text-muted small fw-bold mb-1">{t.lblStart}</label>
                                    <input type="date" className="form-control win10-input" value={dataInicio} onChange={(e) => setDataInicio(e.target.value)} />
                                </div>
                                <div className="d-flex flex-column">
                                    <label className="text-muted small fw-bold mb-1">{t.lblEnd}</label>
                                    <input type="date" className="form-control win10-input" value={dataFim} onChange={(e) => setDataFim(e.target.value)} />
                                </div>
                                
                                <button className="win10-btn-filter" onClick={fetchContaCorrente} disabled={loading || !clienteSelecionado}>
                                    {loading ? <RefreshCw size={14} className="animate-spin" /> : <Calendar size={14}/>} 
                                    {t.btnFilter}
                                </button>

                                <a 
                                    href={pdfUrl} 
                                    target="_blank" 
                                    rel="noopener noreferrer" 
                                    className={`win10-link-pdf ${movimentos.length === 0 ? "disabled" : ""}`}
                                >
                                    <FileText size={14}/>
                                    {t.btnPdf}
                                </a>

                                <button 
                                    className="win10-btn-ticket" 
                                    onClick={abrirModalImpressora} 
                                    disabled={movimentos.length === 0}
                                >
                                    <Printer size={14}/>
                                    {t.btnTicket}
                                </button>
                            </div>
                        </div>

                        <div className="kpi-container">
                            <div className="kpi-box">
                                <div className="kpi-title d-flex align-items-center gap-1"><ArrowUpRight size={13}/> {t.cardDebito}</div>
                                <div className="kpi-value">{formatCurrency(resumo.totalDebito)}</div>
                            </div>
                            <div className="kpi-box kpi-retencao">
                                <div className="kpi-title d-flex align-items-center gap-1"><RefreshCw size={13}/> {t.cardRetencao}</div>
                                <div className="kpi-value" style={{ color: '#c4641c' }}>{formatCurrency(resumo.totalRetencao)}</div>
                            </div>
                            <div className="kpi-box kpi-credito">
                                <div className="kpi-title d-flex align-items-center gap-1"><ArrowDownLeft size={13}/> {t.cardCredito}</div>
                                <div className="kpi-value">{formatCurrency(resumo.totalCredito)}</div>
                            </div>
                            <div className={`kpi-box kpi-saldo ${resumo.saldoDebitoPendente > 0 ? 'kpi-saldo-devedor' : ''}`}>
                                <div className="kpi-title d-flex align-items-center gap-1"><DollarSign size={13}/> {t.cardSaldo}</div>
                                <div className="kpi-value">{formatCurrency(resumo.saldoDebitoPendente)}</div>
                            </div>
                        </div>

                        <div className="mt-3">
                            <DataTable 
                                columns={[
                                    { 
                                        title: t.colData, 
                                        data: "data",
                                        render: (date) => date ? date : "---"
                                    },
                                    { 
                                        title: t.colDoc, 
                                        data: "documento", 
                                        render: (documento, row) => {
                                            return `
                                                <span class="fw-bold text-dark">${documento || 'DOC'}</span>
                                                <div class="text-muted small" style="font-size: 11px; line-height: 14px; margin-top: 2px;">
                                                    ${row.total_iva > 0 ? `<span>IVA: ${formatCurrency(row.total_iva)}</span> | ` : ''}
                                                    ${row.total_iec > 0 ? `<span>IEC: ${formatCurrency(row.total_iec)}</span> | ` : ''}
                                                    ${row.troco > 0 ? `<br/><span class="text-warning">Troco Devolvido: ${formatCurrency(row.troco)}</span>` : ''}
                                                </div>
                                            `;
                                        }
                                    },
                                    { 
                                        title: t.colTipo, 
                                        data: "doc_type",
                                        render: (tipo) => {
                                            return `<span class="badge-cc cc-debito">${tipo}</span>`;
                                        }
                                    },
                                    { 
                                        title: t.colDebito, 
                                        data: "total_geral",
                                        render: (total_geral) => {
                                            return `<span class="font-mono text-danger fw-bold">+${formatCurrency(total_geral)}</span>`;
                                        }
                                    },
                                    { 
                                        title: t.colCredito, 
                                        data: "valor_pago",
                                        render: (valor_pago, row) => {
                                            return `<span class="font-mono text-success fw-bold">-${formatCurrency(row.credito_documento)}</span>`;
                                        }
                                    },
                                    { 
                                        title: t.colSaldoProg, 
                                        data: "saldo_acumulado",
                                        render: (val) => {
                                            const colorClass = val > 0 ? "text-danger" : "text-success fw-bold";
                                            return `<strong class="font-mono ${colorClass}">${formatCurrency(val)}</strong>`;
                                        }
                                    }
                                ]}
                                data={movimentos}
                            />
                        </div>
                    </div>

                </div>
            </main>

            {/* Modal de Impressão */}
            {modalAberto && (
                <div className="win10-modal-overlay">
                    <div className="win10-modal-box">
                        <div className="win10-modal-header">
                            <div>
                                <h5 className="m-0 text-dark fw-bold" style={{fontSize: "14px"}}>{t.modalTitle}</h5>
                            </div>
                            <button className="btn p-0 border-0" onClick={() => setModalAberto(false)} disabled={loadingImpressao}>
                                <X size={18} className="text-muted"/>
                            </button>
                        </div>
                        <div className="win10-modal-body">
                            <p className="text-muted small mb-3">{t.modalSubtitle}</p>
                            
                            {loadingImpressoras ? (
                                <div className="text-center py-4">
                                    <RefreshCw size={24} className="animate-spin text-primary mx-auto" />
                                    <span className="text-muted small mt-2 d-block">Procurando impressoras activas...</span>
                                </div>
                            ) : impressoras.length === 0 ? (
                                <div className="alert alert-warning border-0 small rounded-0">{t.lblNenhumaImpressora}</div>
                            ) : (
                                <div style={{maxHeight: "250px", overflowY: "auto"}}>
                                    {impressoras.map((printer) => (
                                        <div key={printer.id} className="printer-row">
                                            <div>
                                                <div className="fw-bold text-dark small d-flex align-items-center gap-1">
                                                    <Printer size={13}/> {printer.nome.toUpperCase()}
                                                </div>
                                                <small className="text-muted" style={{fontSize: "11px"}}>Caminho / IP: {printer.porta || "Local USB"}</small>
                                            </div>
                                            <button 
                                                className="win10-btn-filter" 
                                                style={{height: "28px", fontSize: "11px", padding: "0 12px"}}
                                                onClick={() => dispararImpressaoTicket(printer.id)}
                                                disabled={loadingImpressao}
                                            >
                                                {loadingImpressao ? "A imprimir..." : t.btnEnviarImpressao}
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            <Footer />
        </div>
    );
};

export default ContaCorrente;
